import { useState, useEffect, useRef } from "react";
import { X, Upload, Loader2, Image as ImageIcon, Search, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PillSwitch } from "@/components/PillSwitch";

const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"];
import { useSubscription } from "@/context/SubscriptionContext";
const GAME_BADGE = { pokemon_japan: "JP", pokemon_china: "CN", custom: "CUSTOM", magic: "MTG", onepiece: "OP", riftbound: "RIFT", lorcana: "LOR", yugioh: "YGO" };
const MANUAL_CATEGORIES = ["pokemon_china", "custom"]; // series with no TCGPlayer catalog — details entered by hand
const empty = {
  name: "", set_name: "", card_number: "", rarity: "", condition: "NM",
  quantity: 1, low_stock_threshold: 1, tcg_price: 0, shopify_price: 0,
  sku: "", image_url: "", category: "pokemon", tcg_product_id: "", tcg_printing: "", tcg_market_price: 0,
  price_override: false, price_basis: "", on_shopify: false, on_tcgplayer: true, item_type: "raw",
};

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const imgUrl = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function CardModal({ open, card, multi = false, onClose, onSaved, onMultiDone }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [printings, setPrintings] = useState([]);
  const [condPrice, setCondPrice] = useState(null);
  const [condLoading, setCondLoading] = useState(false);
  const [addedCount, setAddedCount] = useState(0);
  const [sessionCards, setSessionCards] = useState([]);
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const { gameOptions } = useSubscription();
  const [searchType, setSearchType] = useState("single"); // single | sealed
  const fileRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(card ? { ...empty, ...card } : empty);
      setQuery(""); setResults([]); setShowResults(false);
      setPrintings(card?.tcg_printing ? [{ printing: card.tcg_printing, market: card.tcg_market_price }] : []);
      setAddedCount(0);
      setSessionCards([]);
      setCatFilter(card?.category || "");
      setSearchType(card?.item_type === "sealed" ? "sealed" : "single");
      api.get("/settings").then(({ data }) => setShopifyConnected(!!data?.shopify?.connected)).catch(() => {});
    }
  }, [open, card]);

  useEffect(() => {
    if (open && !card && shopifyConnected) setForm((f) => ({ ...f, on_shopify: true }));
  }, [open, card, shopifyConnected]);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/catalog/search", { params: { q: query, limit: 20, category: catFilter, sealed_only: searchType === "sealed" } });
        setResults(data);
        setShowResults(true);
      } catch { /* ignore */ } finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query, catFilter, searchType]);

  const condKey = `${form.tcg_product_id}|${form.tcg_printing}|${form.condition}`;
  useEffect(() => {
    if (!open || !form.tcg_product_id || form.price_override) return;
    // Fetch the LIVE current price whenever a catalog card / printing / condition is loaded,
    // so a brand-new card is added at today's price (NM -> live TCGPlayer market, others -> tcgapi.dev).
    let cancelled = false;
    setCondLoading(true);
    api.get(`/catalog/live-price/${form.tcg_product_id}`, { params: { condition: form.condition, printing: form.tcg_printing || "Normal" } })
      .then(({ data }) => {
        if (cancelled) return;
        setCondPrice(data);
        setForm((f) => {
          if (f.price_override) return f;
          const upd = {};
          if (data.market != null) upd.tcg_market_price = round2(data.market);
          let chosen = data.price;
          if (f.price_basis === "lowest" && data.lowest != null) chosen = data.lowest;
          else if (f.price_basis === "median" && data.median != null) chosen = data.median;
          if (chosen != null) upd.tcg_price = round2(chosen);
          return { ...f, ...upd };
        });
      })
      .catch(() => { if (!cancelled) setCondPrice(null); })
      .finally(() => { if (!cancelled) setCondLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, condKey, form.price_override]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isManual = MANUAL_CATEGORIES.includes(form.category);

  const onCategoryChange = (v) => {
    setCatFilter(v);
    if (MANUAL_CATEGORIES.includes(v)) {
      setForm((f) => ({ ...f, category: v, tcg_product_id: "", sku: "", tcg_printing: "", tcg_market_price: 0, on_tcgplayer: false, price_override: true }));
      setPrintings([]); setCondPrice(null); setResults([]); setShowResults(false); setQuery("");
    } else {
      setForm((f) => (MANUAL_CATEGORIES.includes(f.category) ? { ...f, category: "pokemon", on_tcgplayer: true, price_override: false } : f));
    }
  };

  // When ending an "Add Multiple" session, offer to print labels for everything added.
  const handleClose = () => {
    if (multi && sessionCards.length) onMultiDone?.(sessionCards);
    onClose();
  };

  const selectCatalog = (r) => {
    const printing = r.printings?.[0];
    setPrintings(r.printings || []);
    setForm((f) => ({
      ...f,
      name: r.name,
      set_name: r.set_name || "",
      card_number: r.number || "",
      rarity: r.rarity || "",
      image_url: r.image_url || "",
      category: r.category || "pokemon",
      tcg_product_id: r.product_id,
      sku: r.product_id, // SKU matches TCGPlayer id
      item_type: searchType === "sealed" ? "sealed" : "raw",
      condition: searchType === "sealed" ? "NM" : f.condition,
      tcg_printing: printing?.printing || "",
      tcg_market_price: printing?.market || 0,
      tcg_price: f.price_override ? f.tcg_price : (f.condition === "NM" ? round2(printing?.market || 0) : f.tcg_price),
      on_tcgplayer: true,
    }));
    setShowResults(false);
    setQuery("");
    toast.success(`Linked to TCGPlayer #${r.product_id}`);
  };

  const selectPrinting = (p) => {
    setForm((f) => ({
      ...f,
      tcg_printing: p.printing,
      tcg_market_price: p.market || 0,
      tcg_price: f.price_override ? f.tcg_price : (f.condition === "NM" ? round2(p.market || 0) : f.tcg_price),
    }));
  };

  const setCondition = (cond) => {
    setForm((f) => ({ ...f, condition: cond, price_basis: "" }));
  };

  const chooseBasis = (b, val) => {
    if (val == null) return;
    setForm((f) => ({ ...f, price_basis: b, tcg_price: round2(val) }));
  };

  const toggleOverride = (v) => {
    setForm((f) => ({ ...f, price_override: v, tcg_price: !v && condPrice?.price != null ? condPrice.price : f.tcg_price }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Card name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 0,
        tcg_price: Number(form.tcg_price) || 0,
        shopify_price: Number(form.tcg_price) || 0,
        tcg_market_price: Number(form.tcg_market_price) || 0,
      };
      let saved = null;
      if (card?.id) { const { data } = await api.put(`/cards/${card.id}`, payload); saved = data; }
      else { const { data } = await api.post("/cards", payload); saved = data; }
      if (multi && !card?.id) {
        onSaved(saved, true);
        setAddedCount((n) => n + 1);
        if (saved?.qr_token) setSessionCards((arr) => [...arr, { ...saved, print_qty: payload.quantity }]);
        toast.success(`Added ${saved?.name || "card"} · ${addedCount + 1} this session`);
        // Reset for the next card, keep the modal open for rapid entry
        setForm(isManual ? { ...empty, category: form.category, on_tcgplayer: false, price_override: true } : empty);
        setQuery(""); setResults([]); setShowResults(false);
        setPrintings([]); setCondPrice(null);
        setTimeout(() => searchRef.current?.focus(), 50);
      } else {
        toast.success(card?.id ? "Card updated" : "Card added");
        onSaved(saved, !card?.id); onClose();
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const imgSrc = imgUrl(form.image_url);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,7,12,0.8)", backdropFilter: "blur(4px)" }}>
      <div className="w-full max-w-2xl rounded-2xl border max-h-[92vh] overflow-y-auto animate-fade-up" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }} data-testid="card-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 z-20" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }}>
          <h3 className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2" data-testid="card-modal-title">
            {card?.id ? "Edit Card" : (multi ? "Add Multiple Cards" : "Add Card")}
            {multi && addedCount > 0 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/40" data-testid="multi-added-count">{addedCount} added</span>
            )}
          </h3>
          <button onClick={handleClose} className="text-slate-500 hover:text-slate-200" data-testid="modal-close-button"><X className="h-5 w-5" /></button>
        </div>

        {/* Catalog search */}
        <div className="px-6 pt-5">
          <div className="flex items-center gap-2 mb-2" data-testid="card-type-toggle">
            {[["single", "Singles"], ["sealed", "Sealed Product"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => { setSearchType(v); setResults([]); setShowResults(false); setQuery(""); }}
                data-testid={`card-type-${v}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${searchType === v ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "text-slate-400 border-[var(--pk-border)] hover:bg-[var(--pk-hover)]"}`}>
                {l}
              </button>
            ))}
          </div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-amber-400" /> {isManual ? (form.category === "custom" ? "Custom item" : "Series") : `Search TCGPlayer catalog${searchType === "sealed" ? " (sealed)" : ""}`}</label>
          <div className="relative mt-1.5">
            <div className="flex gap-2">
              <select value={catFilter} onChange={(e) => onCategoryChange(e.target.value)} data-testid="catalog-category-filter"
                className="px-2.5 py-2.5 rounded-xl border text-sm text-slate-200 outline-none focus:ring-2 focus:ring-amber-500/50 shrink-0" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                {[...gameOptions, ["custom", "Custom item"]].map(([v, l]) => <option key={v} value={v} style={{ background: "#0D1017" }}>{l}</option>)}
              </select>
              {isManual ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border flex-1 text-xs text-amber-300/90" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="manual-entry-note">
                  {form.category === "custom"
                    ? "Custom item — type a name, price & quantity by hand. Not tied to any catalog or market price."
                    : "No TCGPlayer catalog for this series — type the card details and price by hand below."}
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border flex-1 focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                  <Search className="h-4 w-4 text-slate-500 shrink-0" />
                  <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} onFocus={() => results.length && setShowResults(true)}
                    data-testid="catalog-search-input" placeholder="e.g. Charizard Base Set (add the set for vintage cards)"
                    className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600" />
                  {searching && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
                </div>
              )}
            </div>
            {!isManual && showResults && results.length > 0 && (
              <div className="absolute z-30 mt-2 w-full rounded-xl border max-h-80 overflow-y-auto shadow-2xl" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }} data-testid="catalog-results">
                {results.map((r) => (
                  <button key={r.product_id} onClick={() => selectCatalog(r)} data-testid="catalog-result-item"
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--pk-hover)] text-left border-b last:border-0" style={{ borderColor: "var(--pk-border-subtle)" }}>
                    <div className="h-12 w-9 rounded bg-[var(--pk-card)] overflow-hidden shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--pk-border)" }}>
                      {r.image_url ? <img src={r.image_url} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-3 w-3 text-slate-700" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 font-medium truncate flex items-center gap-1.5">{r.name}{GAME_BADGE[r.category] && <span className="shrink-0 px-1 py-0.5 rounded bg-indigo-950/80 border border-indigo-800/60 text-[9px] font-bold text-indigo-300">{GAME_BADGE[r.category]}</span>}</div>
                      <div className="text-[11px] text-slate-500 truncate">{r.set_name} · #{r.number} · {r.rarity}</div>
                    </div>
                    {r.boxed && Object.keys(r.boxed).length > 0 && (
                      <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-950/80 border border-blue-800/60 text-[9px] font-bold text-blue-400" title="Boxed.gg is buying this card">BOXED</span>
                    )}
                    {r.printings?.[0]?.market != null && <span className="font-mono-num text-sm text-amber-400 shrink-0">{fmt(r.printings[0].market)}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!isManual && <p className="text-[11px] text-slate-500 mt-1.5">Selecting a result fills the details, image, SKU (= TCGPlayer id) and market price automatically.</p>}
        </div>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div className="sm:col-span-1">
            <div className="aspect-[3/4] rounded-xl border overflow-hidden flex items-center justify-center" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
              {imgSrc ? <img src={imgSrc} alt="" className="w-full h-full object-contain" /> : <ImageIcon className="h-10 w-10 text-slate-700" />}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleUpload} data-testid="modal-image-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg border text-xs text-slate-300 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload custom image
            </button>
          </div>

          <div className="sm:col-span-2 grid grid-cols-2 gap-4">
            <Input className="col-span-2" label="Card Name" value={form.name} onChange={(v) => set("name", v)} testid="modal-card-name-input" disabled={!isManual} />
            <Input label="Set" value={form.set_name} onChange={(v) => set("set_name", v)} testid="modal-card-set-select" disabled={!isManual} />
            <Input label="Card #" value={form.card_number} onChange={(v) => set("card_number", v)} testid="modal-card-number-input" disabled={!isManual} />
            <Input label="Rarity" value={form.rarity} onChange={(v) => set("rarity", v)} testid="modal-card-rarity-input" disabled={!isManual} />
            <p className="col-span-2 -mt-2 text-[11px] text-slate-500">{isManual ? "Enter the card name, set, number & rarity by hand for this series." : "Name, set, number & rarity are set from the TCGPlayer catalog search above and can't be edited manually."}</p>
            <div>
              <Label>Condition</Label>
              {searchType === "sealed" ? (
                <div className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-400" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="sealed-condition-note">
                  Sealed (N/A)
                </div>
              ) : (
                <select value={form.condition} onChange={(e) => setCondition(e.target.value)} data-testid="modal-card-condition-select"
                  className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none focus:ring-2 focus:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                  {CONDITIONS.map((c) => <option key={c} value={c} style={{ background: "#0D1017" }}>{c}</option>)}
                </select>
              )}
            </div>

            {printings.length > 1 && (
              <div className="col-span-2">
                <Label>Printing</Label>
                <div className="flex flex-wrap gap-2">
                  {printings.map((p) => (
                    <button key={p.printing} type="button" onClick={() => selectPrinting(p)} data-testid={`printing-chip-${p.printing.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-3 py-1.5 rounded-lg text-xs border flex items-center gap-1.5 ${form.tcg_printing === p.printing ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "text-slate-400 border-[var(--pk-border)] hover:bg-[var(--pk-hover)]"}`}>
                      {form.tcg_printing === p.printing && <Check className="h-3 w-3" />}
                      {p.printing} {p.market != null && <span className="font-mono-num">{fmt(p.market)}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Market price + override */}
            {!isManual && (
            <div className="col-span-2 rounded-xl border p-3" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="market-price-panel">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">TCGPlayer Market (NM{form.tcg_printing ? `, ${form.tcg_printing}` : ""})</span>
                <span className="font-mono-num text-lg font-bold text-amber-400" data-testid="modal-nm-market">{form.tcg_market_price ? fmt(form.tcg_market_price) : "—"}</span>
              </div>
              {(form.condition || "NM").toUpperCase() === "NM" ? (
                <>
                  <div className="mt-2 grid grid-cols-2 gap-2" data-testid="nm-price-row">
                    {[["market", "TCGPlayer Market", Number(form.tcg_market_price) || null], ["lowest", "Lowest listed", condPrice ? condPrice.lowest : null]].map(([b, lbl, val]) => {
                      const active = (form.price_basis || "market") === b;
                      return (
                        <button key={b} type="button" disabled={val == null || condLoading}
                          onClick={() => chooseBasis(b, val)} data-testid={`price-basis-${b}`}
                          className={`flex flex-col items-start px-3 py-2 rounded-lg border transition-colors ${active ? "border-amber-500/60 bg-amber-500/10" : "border-[var(--pk-border)] hover:bg-[var(--pk-hover)]"} ${val == null ? "opacity-40 cursor-not-allowed" : ""}`}
                          style={active ? {} : { borderColor: "var(--pk-border)" }}>
                          <span className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                            {lbl}{active && <Check className="h-3 w-3 text-amber-400" />}
                          </span>
                          <span className={`font-mono-num text-sm font-semibold ${active ? "text-amber-400" : "text-slate-200"}`} data-testid={`price-basis-${b}-value`}>
                            {b === "lowest" && condLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : val != null ? fmt(val) : "—"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5" data-testid="modal-nm-note">
                    Near Mint defaults to the live TCGPlayer market price. Tap "Lowest listed" (tcgapi.dev) to price off the lowest NM listing instead.
                  </p>
                </>
              ) : (
                <>
                  <div className="mt-2 grid grid-cols-2 gap-2" data-testid="condition-price-row">
                    {["median", "lowest"].map((b) => {
                      const val = condPrice ? condPrice[b] : null;
                      const active = (form.price_basis || condPrice?.source || "median") === b;
                      return (
                        <button key={b} type="button" disabled={val == null || condLoading}
                          onClick={() => chooseBasis(b, val)} data-testid={`price-basis-${b}`}
                          className={`flex flex-col items-start px-3 py-2 rounded-lg border transition-colors ${active ? "border-amber-500/60 bg-amber-500/10" : "border-[var(--pk-border)] hover:bg-[var(--pk-hover)]"} ${val == null ? "opacity-40 cursor-not-allowed" : ""}`}
                          style={active ? {} : { borderColor: "var(--pk-border)" }}>
                          <span className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                            {b === "median" ? "Median listed" : "Lowest listed"}{active && <Check className="h-3 w-3 text-amber-400" />}
                          </span>
                          <span className={`font-mono-num text-sm font-semibold ${active ? "text-amber-400" : "text-slate-200"}`} data-testid={`price-basis-${b}-value`}>
                            {condLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : val != null ? fmt(val) : "—"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5">{form.condition} listings via tcgapi.dev — tap to choose which price to use.</p>
                  {!condLoading && form.tcg_product_id && condPrice && condPrice.median == null && condPrice.lowest == null && (
                    <p className="text-[11px] text-amber-400/90 mt-1" data-testid="modal-noncondition-note">
                      No {form.condition} listings found for this printing — enter the price manually below.
                    </p>
                  )}
                </>
              )}
              <label className="flex items-center gap-2 mt-3 cursor-pointer select-none" data-testid="price-override-toggle">
                <input type="checkbox" checked={form.price_override} onChange={(e) => toggleOverride(e.target.checked)} className="accent-amber-500 h-4 w-4" />
                <span className="text-sm text-slate-300">Set price manually (exclude from auto-repricing)</span>
              </label>
            </div>
            )}

            <div>
              <Label>Your TCG Price ($)</Label>
              <input type="number" value={form.tcg_price} onChange={(e) => set("tcg_price", e.target.value)} disabled={!isManual && !form.price_override && (((form.condition || "NM").toUpperCase() === "NM" && form.tcg_market_price > 0) || condPrice?.price != null)}
                data-testid="modal-card-tcg-price-input"
                className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none focus:ring-2 focus:ring-amber-500/50 font-mono-num disabled:opacity-60"
                style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} />
            </div>
            <div>
              <Label>Shopify Price ($)</Label>
              <div className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-300 font-mono-num flex items-center justify-between" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="modal-card-shopify-price-display">
                <span>{fmt(form.tcg_price)}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-600">= TCG price</span>
              </div>
            </div>
            <Input label="Quantity" type="number" value={form.quantity} onChange={(v) => set("quantity", v)} testid="modal-card-qty-input" />
            <Input label="Low-stock alert at" type="number" value={form.low_stock_threshold} onChange={(v) => set("low_stock_threshold", v)} />
            <Input className="col-span-2" label="SKU / TCGplayer Id" value={form.sku} onChange={(v) => set("sku", v)} />
            <div className="col-span-2 flex gap-5 pt-1">
              <PillSwitch label="On TCGPlayer" checked={form.on_tcgplayer} onChange={(v) => set("on_tcgplayer", v)} testid="modal-toggle-tcgplayer" />
              <PillSwitch label="On Shopify" checked={form.on_shopify} onChange={(v) => set("on_shopify", v)} testid="modal-toggle-shopify" />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3 sticky bottom-0" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }}>
          <button onClick={handleClose} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-[var(--pk-hover)] border" style={{ borderColor: "var(--pk-border)" }} data-testid="modal-cancel-button">{multi && addedCount > 0 ? "Done & Print Labels" : "Cancel"}</button>
          <button onClick={handleSave} disabled={saving} data-testid="modal-save-card-button" className="px-5 py-2 rounded-lg text-sm font-semibold bg-amber-500 text-[#1a1205] hover:bg-amber-400 disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {card?.id ? "Save Card" : (multi ? "Add & Next" : "Save Card")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-1.5">{children}</span>;
}
function Input({ label, value, onChange, type = "text", testid, className = "", disabled = false }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled}
        className="w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none focus:ring-2 focus:ring-amber-500/50 font-mono-num disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} />
    </div>
  );
}
